<template>
    <modal
        v-if="show"
        :form="form"
        size="is-small"
        type="is-primary"
        :title="titleModal"
        class="has-selectize"
        :submit-text="titleSubmitModal"
        success-text="Message Credits Added"
        :disableOverflow="true"
        @submit="addFunds"
        @closed="closed"
    >
        <div class="content">
            <div v-if="form.errors.has('form.0')" class="message is-warning">
                <div class="message-body">
                    {{ form.errors.get('form.0') }}
                </div>
            </div>

            <form-message
                v-if="broadcastAmountCredits"
                icon="info-circle"
                type="is-info"
                :message="getFormMessage"
            />

            <p>Message credits are used to send and receive messages.</p>

            <recharge-options
                :value="form.amount"
                label="Select amount to purchase"
                placeholder="Select Amount"
                :error="form.errors.get('quantity.0')"
                optionLabel="message credits"
                @input="updateAmount"
            />

            <div class="field">
                <plan-tax-line v-if="taxLineData" :prorate="taxLineData" paddingless />
            </div>

            <div class="field">
                <label class="label">Select Credit Card</label>
                <p class="control">
                    <form-dropdown
                        id="dropdown-source"
                        :key="sourceKey"
                        v-model="form.source"
                        :placeholder="'Select Credit Card'"
                        :error="form.errors.get('payment_method.0')"
                        :settings="cardDropdownAutoRechargeSettings"
                    />
                </p>
            </div>

            <credits-auto-recharge v-if="toggleRechargeOnLoad" />
            <p
                v-if="form.errors.get('card_error.0')"
                class="help has-text-danger"
                v-text="form.errors.get('card_error.0')"
            />
        </div>
    </modal>
</template>

<script>
import { mapState } from 'pinia';
import stores from 'store/modules';
import { v4 as uuidv4 } from 'uuid';
import Form from 'core/Form';
import cards from 'mixins/cards';
import OrganizationMixin from 'mixins/organization';
import AutoRechargeMixin from 'mixins/auto-recharge';
import CreditsAutoRecharge from '.././Broadcasts/CreditsAutoRecharge';
import PlanTaxLine from './components/PlanTaxLine.vue';
import RechargeOptions from './components/RechargeOptions.vue';

export default {
    emits: ['closed'],

    components: {
        RechargeOptions,
        PlanTaxLine,
        CreditsAutoRecharge,
    },

    mixins: [cards, OrganizationMixin, AutoRechargeMixin],

    props: {
        show: { type: Boolean, required: true },
    },

    data: () => ({
        form: new Form(
            {
                amount: 0,
                source: '',
            },
            false
        ),
        taxLineData: null,
        toggleRechargeOnLoad: false,
        sourceKey: '',
    }),

    computed: {
        titleModal() {
            return 'Add Credits';
        },

        titleSubmitModal() {
            return 'Add Credits';
        },

        getFormMessage() {
            if (this.broadcastAmountCredits) {
                return `Oops, you're almost out of credits. To send your broadcast,
                please purchase a minimum of ${this.broadcastAmountCredits} credits.`;
            }

            return '';
        },

        primary() {
            return this.billingSources.filter((item) => item.primary === true);
        },

        ...mapState(stores.BillingSources, [
            'billingSources',
            'billingSourcesSimple',
            'rechargeOptions',
            'rechargeOptionsLoading',
        ]),
        ...mapState(stores.Broadcasts, ['broadcastAmountCredits']),
        ...mapState(stores.AutoRecharge, ['autoRecharge']),
    },

    watch: {
        primary() {
            this.reInitDropdown();
        },
    },

    methods: {
        reInitDropdown() {
            if (this.primary && this.primary[0]) {
                this.form.source = this.primary[0].id;
            }
        },

        closed() {
            this.$emit('closed');
            this.form.reset();
            this.$store.dispatch('changeBroadcastAmountCredits', 0);
            this.reInitDropdown();
        },

        async addFunds() {
            try {
                const params = {
                    quantity: this.form.amount,
                    payment_method: this.form.source ? this.form.source : '',
                };
                this.form.busy = true;
                await this.$store.dispatch('updateBillingCreditsMicroservice', params);

                this.form.successful = true;
                setTimeout(() => {
                    this.closed();
                }, 1500);
            } catch (e) {
                this.$log.error(e, this.$options.__file, 'addFunds');

                const response = e.response.data;

                if (response.errors.quantity) {
                    this.form.errors.record({ quantity: [response.errors.quantity] });
                }

                if (response.errors.payment_method) {
                    if (response.errors.payment_method[0] === 'Your card was declined.') {
                        this.form.errors.record({
                            form: [
                                response.errors.payment_method ||
                                    'Something went wrong, please try again.',
                            ],
                        });
                    } else
                        this.form.errors.record({
                            payment_method: [response.errors.payment_method],
                        });
                }

                if (response.errors.quantity && response.errors.payment_method) {
                    this.form.errors.record({
                        quantity: [response.errors.quantity],
                        payment_method: [response.errors.payment_method],
                    });
                }
            } finally {
                this.form.busy = false;
            }
        },

        init() {
            if (this.primary && this.primary[0]) {
                this.form.source = this.primary[0].id;
            }
            this.sourceKey = uuidv4();
        },

        updateAmount(value, option) {
            if (option) {
                this.taxLineData = {
                    tax_rates: [{ jurisdiction: option?.jurisdiction || '' }],
                    taxes_amount: option?.taxes_amount || 0,
                };
            }

            this.form.amount = value;
        },
    },

    mounted() {
        this.init();
    },

    updated() {
        this.toggleRechargeOnLoad = this.autoRecharge && !this.autoRecharge.is_active;
    },
};
</script>
